<template>
  <MainPanelMenu ref="menuRef" v-model:expandedKeys="expandedKeys" :model="mappedMenuItems" />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useFavoritesStore } from '@/stores/favorites';
import { useProfileStore } from '@/stores/profile';
import { isAllowed } from '@/router/navigation';
import {
  countMenuItems,
  filterNavigationMenu,
  findNavigationItem,
  header,
  item,
  type NavMenuItem,
  type PanelMenuItem,
  removeEmptyHeaders,
} from '@/router/navigation';

import MainPanelMenu from '../components/MainPanelMenu.vue';

const favoritesStore = useFavoritesStore();
const profileStore = useProfileStore();
const { t } = useI18n();

const isSeyboldUser = computed(() => profileStore.userProfile?.isSeyboldUser ?? false);
const addFavoritesToMenu = (menu: NavMenuItem[]): NavMenuItem[] => {
  const favorites = favoritesStore.favorites;
  const favoriteItems = favorites
    .map((favKey) => {
      const favItem = findNavigationItem(favKey);

      if (!favItem?.name) {
        return null;
      }

      if (!isAllowed(favItem, isSeyboldUser.value)) {
        return null;
      }

      return item(favItem);
    })
    .filter((item): item is NavMenuItem => item !== null);

  if (favoriteItems.length === 0) {
    return menu;
  }

  const favoritesHeader = {
    ...header(findNavigationItem('favorites')),
    children: favoriteItems,
  };

  return [favoritesHeader, ...menu];
};

const removeFavoritesFromMenu = (menu: any[]): any[] => {
  const favorites = favoritesStore.favorites;

  return menu
    .map((item) => {
      if (favorites.includes(item.name)) {
        return null;
      }

      const children = item.children ? removeFavoritesFromMenu(item.children) : null;

      return {
        ...item,
        children: children && children.length > 0 ? children : null,
      };
    })
    .filter((item) => item !== null);
};

const mapNavigationMenu = (menu: NavMenuItem[]): PanelMenuItem[] => {
  return menu?.map((menuItem) => {
    const mappedItem: PanelMenuItem = {
      key: menuItem.name,
      label: t(menuItem.title),
      icon: menuItem.icon,
      route: 'route' in menuItem ? menuItem.route : undefined,
    };

    if ('children' in menuItem && menuItem.children) {
      mappedItem.items = mapNavigationMenu(menuItem.children);
    }

    return mappedItem;
  });
};

const computedFilteredNavigationMenu = computed(() =>
  removeEmptyHeaders(
    addFavoritesToMenu(removeFavoritesFromMenu(filterNavigationMenu(isSeyboldUser.value))),
  ),
);

const mappedMenuItems = computed(() => mapNavigationMenu(computedFilteredNavigationMenu.value));
const expandedKeys = ref<Record<string, boolean>>({});
const menuRef = ref();

const expandAllMenu = () => {
  menuRef.value?.expandAll();
};

const expandRootItemsMenu = () => {
  menuRef.value?.expandRootItems();
};

const expandFavorites = () => {
  menuRef.value?.expandRootByIndex(0);
};

onMounted(() => {
  const totalMenuItemCount = countMenuItems(computedFilteredNavigationMenu.value);

  if (totalMenuItemCount <= 10) {
    expandAllMenu();
  } else if (favoritesStore.favorites.length) {
    expandFavorites();
  } else {
    expandRootItemsMenu();
  }
});

const didInitExpansion = ref(false);

watch(
  () => profileStore.userProfile,
  async (p) => {
    if (!p) return;

    if (!didInitExpansion.value) {
      await nextTick();
      expandRootItemsMenu();
      didInitExpansion.value = true;
    }
  },
  { immediate: true, flush: 'post' },
);
</script>

<style scoped lang="css"></style>
